import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Select, DatePicker, Button, message, Modal, Tooltip, Tabs, Divider, Typography } from 'antd';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend, ChartData } from 'chart.js';
import { TrendingUp, TrendingDown, Target, X, Plus } from 'lucide-react';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_API_ENDPOINT } from '../utils';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { useMetaSetup } from '../context/MetaSetupContext';
import { useAuthOrg } from '../context/AuthOrgContext';
import { handleApiError } from '../utils/errorHandler';
import { hasRolePermission } from '../utils/roles';
import { Dayjs } from 'dayjs';
import { LoadingOutlined, SyncOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ClientDetails } from '../interfaces/setup';
import CommonLayout from '../common/LayoutBlock';
import AdComparisonTable from './AdComparisionTable';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

const { RangePicker } = DatePicker;
const { Option } = Select;

// Types
interface Campaign {
    campaignId: string;
    campaignName: string;
    botId?: string;
    wabaNumber?: string;
}

interface Filters {
    orgId: string;
    adAccountId: string;
    campaignId: string;
    adsetId: string;
    adId: string;
    dateRange: [Dayjs | null, Dayjs | null];
    adAccountName: string;
}

interface Performance {
    impressions: string | null;
    reach: string | null;
    clicks: string | null;
    uniqueClicks: string | null;
    lead: string | null;
    totalConversation?: string | null;
    actions?: Action[];
}

interface Action {
    actionType: string;
    value: string;
}

interface Cost {
    dailyBudget: string | null;
    lifetimeBudget: string | null;
    spend: string | null;
    ctr: string | null;
    cpc: string | null;
    cpm: string | null;
    leads?: string | null;
    eCostConversion?: string | null;
    costPerLead?: string | null;
}

interface BotMessages {
    userInitiated: number | null;
    botInitiated: number | null;
    total: number | null;
}

interface MilestoneCount {
    milestoneNum: number;
    milestonePositioner: string;
    milestoneName: string;
    userCount: number;
}

interface AllCampaignBotInfo {
    botId: string;
    name: string;
    live: boolean;
}

interface BotAnalytics {
    botId: string;
    botName: string;
    live: boolean;
    milestoneUserCounts: MilestoneCount[];
    messages: BotMessages;
}

interface CampaignBotAnalytics {
    botId: string;
    botName: string;
    campaignId: string;
    campaignName: string;
    live: boolean;
    milestoneUserCounts: MilestoneCount[];
}

interface AllCampaignBotAnalytics {
    bots: AllCampaignBotInfo[];
    analytics: CampaignBotAnalytics[];
}

// Initial States
const INITIAL_PERFORMANCE: Performance = {
    impressions: null,
    reach: null,
    clicks: null,
    uniqueClicks: null,
    lead: null,
    totalConversation: null,
    actions: [],
};

const INITIAL_COST: Cost = {
    dailyBudget: null,
    lifetimeBudget: null,
    spend: null,
    ctr: null,
    cpc: null,
    cpm: null,
    leads: null,
    eCostConversion: null,
    costPerLead: null,
};

const INITIAL_BOT_ANALYTICS: BotAnalytics = {
    botId: '',
    botName: '',
    live: false,
    milestoneUserCounts: [],
    messages: { userInitiated: 0, botInitiated: 0, total: 0 },
};

const INITIAL_ALL_CAMPAIGN_BOT_ANALYTICS: AllCampaignBotAnalytics = {
    bots: [],
    analytics: [],
};

type MetricCardProps = {
    title: string;
    value: React.ReactNode;
    subtitle?: string;
    tooltip?: string;
    disabled?: boolean;
};

type MetricValue = string | number | null | undefined;

const normalizeMetricKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const parseMetricValue = (value: MetricValue): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const formatNumber = (value: MetricValue, fractionDigits = 0) => {
    const parsed = parseMetricValue(value);
    if (parsed === null) return '-';

    return parsed.toLocaleString('en-IN', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
};

const formatCurrency = (value: MetricValue, fractionDigits = 0) => {
    const parsed = parseMetricValue(value);
    if (parsed === null) return '₹-';

    return `₹${formatNumber(parsed, fractionDigits)}`;
};

const formatPercent = (value: MetricValue, fractionDigits = 2) => {
    const parsed = parseMetricValue(value);
    if (parsed === null) return '-';

    return `${formatNumber(parsed, fractionDigits)}%`;
};

const calculateRate = (numerator: MetricValue, denominator: MetricValue) => {
    const parsedNumerator = parseMetricValue(numerator);
    const parsedDenominator = parseMetricValue(denominator);
    if (parsedNumerator === null || parsedDenominator === null || parsedDenominator === 0) return null;

    return (parsedNumerator / parsedDenominator) * 100;
};

const calculateCost = (spend: MetricValue, count: MetricValue) => {
    const parsedSpend = parseMetricValue(spend);
    const parsedCount = parseMetricValue(count);
    if (parsedSpend === null || parsedCount === null || parsedCount === 0) return null;

    return parsedSpend / parsedCount;
};

const MetaAnalyticsDashboard: React.FC = () => {
    const { selectedAdAccount, orgWabaData, adAccountList, metaConnectStatus } = useMetaSetup();
    const { currentOrg, availableOrgs, user } = useAuthOrg();
    const { setBreadcrumbs } = useBreadcrumbs();
    const [searchParams] = useSearchParams();

    // State
    const [loading, setLoading] = useState(false);
    const [loading2, setLoading2] = useState(false);
    const [activeTab, setActiveTab] = useState('1');
    const [campaigns, setCampaigns] = useState<Campaign[]>([
        { campaignId: 'all', campaignName: 'All Campaigns' },
    ]);

    const [wabaDetails, setWabaDetails] = useState<ClientDetails[]>([]);
    const [selectedWabaId, setSelectedWabaId] = useState<string>('');

    const [overviewFilters, setOverviewFilters] = useState<Filters>({
        orgId: '',
        adAccountId: '',
        campaignId: 'all',
        adsetId: 'all',
        adId: 'all',
        dateRange: [null, null],
        adAccountName: '',
    });

    const [comparisonFilters, setComparisonFilters] = useState<Filters>({
        orgId: currentOrg?.orgId || '',
        adAccountId: selectedAdAccount?.id || '',
        campaignId: 'all',
        adsetId: 'all',
        adId: 'all',
        dateRange: [null, null],
        adAccountName: selectedAdAccount?.name || '',
    });

    const [performance, setPerformance] = useState<Performance>(INITIAL_PERFORMANCE);
    const [cost, setCost] = useState<Cost>(INITIAL_COST);
    const [comparisonList, setComparisonList] = useState<any[]>([]);
    const [botAnalytics, setBotAnalytics] = useState<BotAnalytics>(INITIAL_BOT_ANALYTICS);
    const [allCampaignBotAnalytics, setAllCampaignBotAnalytics] = useState<AllCampaignBotAnalytics>(INITIAL_ALL_CAMPAIGN_BOT_ANALYTICS);
    const [hasFetchedAllCampaignBotAnalytics, setHasFetchedAllCampaignBotAnalytics] = useState(false);

    const [adComparisonData, setAdComparisonData] = useState<any[]>([]);
    const [adComparisonCampaignData, setAdComparisonCampaignData] = useState<any>({})
    const [adComparisonLoading, setAdComparisonLoading] = useState(false);

    const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);

    const canSelectMetaAdAccountInReports = hasRolePermission(user?.userRole, 'selectMetaAdAccountInReports');

    const resetAnalyticsState = useCallback(() => {
        setPerformance(INITIAL_PERFORMANCE);
        setCost(INITIAL_COST);
        setBotAnalytics(INITIAL_BOT_ANALYTICS);
        setAllCampaignBotAnalytics(INITIAL_ALL_CAMPAIGN_BOT_ANALYTICS);
        setHasFetchedAllCampaignBotAnalytics(false);
    }, []);

    // API Calls
    const fetchCampaignData = async (adAccountId: string) => {
        try {
            setLoading(true);
            resetAnalyticsState();
            const orgId = activeTab === '1' || activeTab === '3' ? overviewFilters.orgId : comparisonFilters.orgId || currentOrg?.orgId;
            if (!orgId) {
                setLoading(false);
                return;
            }

            const res = await coreAPIService.get<any>(
                CTW_API_ENDPOINT,
                `v2/org/dropdown?org_id=${orgId}&ad_account_id=${adAccountId}`
            );

            if (res?.meta?.code === 200 && res.data) {
                const seen = new Set();

                const uniqueCampaigns = res.data
                    .filter((campaign: any) => {
                        if (seen.has(campaign.campaignId)) {
                            return false;
                        }

                        seen.add(campaign.campaignId);
                        return true;
                    })
                    .map((campaign: any) => ({
                        campaignId: campaign.campaignId,
                        campaignName: campaign.campaignName,
                    }));

                setCampaigns([
                    { campaignId: 'all', campaignName: 'All Campaigns' },
                    ...uniqueCampaigns,
                ]);
            }
        } catch (error) {
            handleApiError(error, `v2/org/dropdown`, 'Error loading campaigns');
        } finally {
            setLoading(false);
        }
    };

    const fetchBotAnalytics = useCallback(async (clientId: string, botId?: string) => {
        if (!clientId) return;

        const params = new URLSearchParams();
        // if (botId) params.append('botId', botId);
        // if (currentOrg?.orgId) params.append('orgId', currentOrg?.orgId || "");
        if (overviewFilters.dateRange[0]) {
            params.append('start_date', overviewFilters.dateRange[0].format('YYYY-MM-DD'));
            params.append('startDate', overviewFilters.dateRange[0].format('YYYY-MM-DD'));
            if (overviewFilters.dateRange[1]) {
                params.append('end_date', overviewFilters.dateRange[1].format('YYYY-MM-DD'));
                params.append('endDate', overviewFilters.dateRange[1].format('YYYY-MM-DD'));
            }
        }

        try {
            const res = await coreAPIService.get<any>(
                CTW_API_ENDPOINT,
                `v2/bot-analytics/campaign/${overviewFilters.campaignId}?${params.toString()}`
            );
            if (res?.meta?.code === 200 && res.data?.analytics) {
                setBotAnalytics(res.data.analytics);
            }
        } catch (error) {
            // Silent error
        }
    }, [overviewFilters.campaignId, overviewFilters.dateRange]);

    const fetchAllCampaignBotAnalytics = useCallback(async (filters: Filters) => {
        const orgId = filters.orgId || currentOrg?.orgId || '';
        const adAccountId = filters.adAccountId;

        if (!orgId || !adAccountId) {
            setAllCampaignBotAnalytics(INITIAL_ALL_CAMPAIGN_BOT_ANALYTICS);
            setHasFetchedAllCampaignBotAnalytics(false);
            return;
        }

        setHasFetchedAllCampaignBotAnalytics(true);

        const params = new URLSearchParams({
            org_id: orgId,
            ad_account_id: adAccountId,
        });

        if (filters.dateRange[0]) {
            params.append('start_date', filters.dateRange[0].format('YYYY-MM-DD'));
            params.append('startDate', filters.dateRange[0].format('YYYY-MM-DD'));
        }

        if (filters.dateRange[1]) {
            params.append('end_date', filters.dateRange[1].format('YYYY-MM-DD'));
            params.append('endDate', filters.dateRange[1].format('YYYY-MM-DD'));
        }

        try {
            const res = await coreAPIService.get<any>(
                CTW_API_ENDPOINT,
                `v2/bot-analytics/campaign?${params.toString()}`
            );

            if (res?.meta?.code === 200 && res.data) {
                const bots = (res.data.bots ?? []).map((bot: any) => ({
                    botId: bot.botId ?? '',
                    name: bot.name ?? bot.botName ?? 'Unnamed Bot',
                    live: Boolean(bot.live ?? bot.status),
                }));

                const analytics = (res.data.analytics ?? []).map((item: any) => ({
                    botId: item.botId ?? '',
                    botName: item.botName ?? item.name ?? 'Unnamed Bot',
                    campaignId: item.campaignId ?? '',
                    campaignName: item.campaignName ?? 'Unnamed Campaign',
                    live: Boolean(item.live),
                    milestoneUserCounts: (item.milestoneUserCounts ?? []).map((milestone: any) => ({
                        milestoneNum: milestone.milestoneNum ?? 0,
                        milestonePositioner: milestone.milestonePositioner ?? '',
                        milestoneName: milestone.milestoneName ?? 'Unknown',
                        userCount: milestone.userCount ?? 0,
                    })),
                }));

                setAllCampaignBotAnalytics({ bots, analytics });
            } else {
                setAllCampaignBotAnalytics(INITIAL_ALL_CAMPAIGN_BOT_ANALYTICS);
            }
        } catch (error) {
            setAllCampaignBotAnalytics(INITIAL_ALL_CAMPAIGN_BOT_ANALYTICS);
            handleApiError(error, 'v2/bot-analytics/campaign', 'Error fetching all campaign bot analytics');
        }
    }, [currentOrg?.orgId]);

    const handleFilterChange = useCallback(
        (key: keyof Filters, value: any, type: 'overview' | 'comparison' = 'overview') => {
            const setF = type === 'overview' ? setOverviewFilters : setComparisonFilters;
            setF((prev) => {
                const newF = { ...prev, [key]: value };
                if (key === 'adAccountId' && value !== prev.adAccountId) {
                    const accName = adAccountList.find(a => a.id === value)?.name || '';
                    return { ...newF, campaignId: 'all', adAccountName: accName };
                }
                return newF;
            });
        },
        [adAccountList]
    );

    const handleSubmit = useCallback(async () => {
        const currentFilters = activeTab === '1' ? overviewFilters : comparisonFilters;
        if (!currentFilters.adAccountId) return;

        if (activeTab === '2' && comparisonList.length >= 3) {
            message.warning('You can only compare up to 3 campaigns');
            return;
        }

        setLoading2(true);
        const paramsObj: Record<string, string> = { account_id: currentFilters.adAccountId };
        if (currentFilters.campaignId !== 'all') paramsObj.campaign_id = currentFilters.campaignId;
        if (currentFilters.dateRange[0]) {
            paramsObj.start_date = currentFilters.dateRange[0].format('YYYY-MM-DD');
            if (currentFilters.dateRange[1]) {
                paramsObj.end_date = currentFilters.dateRange[1].format('YYYY-MM-DD');
            }
        }
        paramsObj.org_id = currentFilters.orgId || currentOrg?.orgId || "";
        const qs = new URLSearchParams(paramsObj).toString();

        try {
            const [perfRes, costRes] = await Promise.all<[any, any]>([
                coreAPIService.get(CTW_API_ENDPOINT, `v2/ad-analytics/get-performance?${qs}`),
                coreAPIService.get(CTW_API_ENDPOINT, `v2/ad-analytics/cost-analytics?${qs}`),
            ]);

            if (perfRes?.meta?.code === 200 && perfRes.data) {
                const actions = perfRes.data.actions ?? [];
                const linkClicks = actions.find(
                    (action: any) => action.actionType === "link_click"
                );
                setPerformance({
                    ...perfRes.data,
                    actions,
                    clicks: linkClicks?.value ?? 0,
                });
                if (activeTab === '1') {
                    const conversation = actions.find(
                        (action: any) => action.actionType === "onsite_conversion.messaging_conversation_started_7d"
                    );

                    setPerformance({
                        ...perfRes.data,
                        actions: [...actions, { "actionType": "clicks", "value": perfRes.data.clicks }],
                        clicks: linkClicks?.value ?? 0,
                        totalConversation: conversation?.value ?? 0,
                    });
                } else {
                    const campaignName = campaigns.find(c => c.campaignId === currentFilters.campaignId)?.campaignName || currentFilters.campaignId;

                    let creativeThumbnailUrl = '';
                    try {
                        const adsetsRes = await coreAPIService.get<any>(CTW_API_ENDPOINT, `v2/meta-integration/campaigns/${currentFilters.campaignId}/adsets`);
                        if (adsetsRes?.meta?.code === 200 && adsetsRes.data?.length > 0) {
                            const firstAdsetId = adsetsRes.data[0].id;
                            const adsRes = await coreAPIService.get<any>(CTW_API_ENDPOINT, `v2/meta-integration/adsets/${firstAdsetId}/ads-with-fields`);
                            if (adsRes?.meta?.code === 200 && adsRes.data?.length > 0) {
                                creativeThumbnailUrl = adsRes.data[0].creativeThumbnailUrl;
                            }
                        }
                    } catch (err) {
                        console.error('Error fetching creative details:', err);
                    }

                    setComparisonList(prev => [
                        ...prev,
                        {
                            ...perfRes.data,
                            ...costRes?.data,
                            campaignName,
                            campaignId: currentFilters.campaignId,
                            creativeThumbnailUrl,
                            dateRangeFilter: {
                                startDate: currentFilters.dateRange[0] ? currentFilters.dateRange[0].format('DD MMM YYYY') : null,
                                endDate: currentFilters.dateRange[1] ? currentFilters.dateRange[1].format('DD MMM YYYY') : null,
                            },
                            key: Date.now()
                        }
                    ]);
                    setIsComparisonModalOpen(false);
                }

            }

            if (activeTab === '1') {
                if (currentFilters.campaignId === 'all') {
                    setBotAnalytics(INITIAL_BOT_ANALYTICS);
                    await fetchAllCampaignBotAnalytics(currentFilters);
                } else if (perfRes?.meta?.code === 200 && perfRes.data?.botIds && perfRes.data?.wabaNumbers) {
                    setAllCampaignBotAnalytics(INITIAL_ALL_CAMPAIGN_BOT_ANALYTICS);
                    setHasFetchedAllCampaignBotAnalytics(false);
                    setSelectedWabaId(perfRes.data.wabaNumbers[0]);
                    await fetchBotAnalytics(perfRes.data.wabaNumbers[0], perfRes.data.botIds[0]);
                } else {
                    setBotAnalytics(INITIAL_BOT_ANALYTICS);
                    setAllCampaignBotAnalytics(INITIAL_ALL_CAMPAIGN_BOT_ANALYTICS);
                    setHasFetchedAllCampaignBotAnalytics(false);
                }
            }

            if (costRes?.meta?.code === 200 && costRes.data) {
                if (activeTab === '1') setCost(costRes.data);
            }
        } catch (error) {
            handleApiError(error, 'v2/ad-analytics', 'Error fetching analytics');
        } finally {
            setLoading2(false);
        }
    }, [overviewFilters, comparisonFilters, activeTab, campaigns, currentOrg, comparisonList, fetchAllCampaignBotAnalytics, fetchBotAnalytics]);

    const handleAdComparisonSubmit = useCallback(async () => {
        if (!overviewFilters.adAccountId || !overviewFilters.campaignId || overviewFilters.campaignId === 'all') {
            message.warning('Please select a specific Meta Ad Account and Campaign for Ad comparison');
            return;
        }

        setAdComparisonLoading(true);
        try {
            const paramsObj: Record<string, string> = {
                account_id: overviewFilters.adAccountId,
                org_id: overviewFilters.orgId || currentOrg?.orgId || ""
            };

            if (overviewFilters.dateRange[0]) {
                paramsObj.start_date = overviewFilters.dateRange[0].format('YYYY-MM-DD');
                if (overviewFilters.dateRange[1]) {
                    paramsObj.end_date = overviewFilters.dateRange[1].format('YYYY-MM-DD');
                }
            }
            const qs = new URLSearchParams(paramsObj).toString();

            const res = await coreAPIService.get<any>(
                CTW_API_ENDPOINT,
                `v2/reporting/campaigns/${overviewFilters.campaignId}/funnel?${qs}`
            );
            // const res = {
            //     meta: {
            //         code: 200,
            //         status: true
            //     },
            //     data: {
            //         campaignId: "120243469444980365",
            //         campaignName: "MetaGrow_FB_SIP_07Mar",
            //         dateRange: "2026-04-21 to 2026-04-29",
            //         botId: "120243469444980365",
            //         ads: [
            //             {
            //                 adId: "120243469446250365",
            //                 adName: "SIP_1_ad_54MetaGrow_FB_SIP_07MarMetaGrow _FB_SIP_07MarMetaGrow_FB_SIP_07MarMeta Grow_FB_SIP_07Mar",
            //                 spend: 46084,
            //                 reach: 16804,
            //                 impressions: 31930,
            //                 clicks: 372,
            //                 clids: 48,
            //                 milestones: [
            //                     { milestoneName: "L1", milestoneNum: 6, totalCount: 14, uniqueCount: 14 },
            //                     { milestoneName: "l2", milestoneNum: 8, totalCount: 14, uniqueCount: 14 },
            //                     { milestoneName: "asdfd", milestoneNum: 9, totalCount: 14, uniqueCount: 14 },
            //                 ],
            //             },
            //             {
            //                 adId: "120249447580365",
            //                 adName: "SIP_2_xfad_54",
            //                 spend: 964,
            //                 reach: 7170,
            //                 impressions: 9532,
            //                 clicks: 81,
            //                 clids: 13,
            //                 milestones: [
            //                     { milestoneName: "RawLead", milestoneNum: 33, totalCount: 13, uniqueCount: 8 },
            //                     { milestoneName: "DeepEngaged", milestoneNum: 432, totalCount: 8, uniqueCount: 6 },
            //                     { milestoneName: "LeadSubmitted", milestoneNum: 3543, totalCount: 4, uniqueCount: 3 },
            //                     { milestoneName: "LeadSubmitted", milestoneNum: 0, totalCount: 4, uniqueCount: 3 },
            //                 ],
            //             },
            //             {
            //                 adId: "1243469447580365",
            //                 adName: "SIP_2_adsdf_54",
            //                 spend: 964,
            //                 reach: 7170,
            //                 impressions: 9532,
            //                 clicks: 81,
            //                 clids: 13,
            //                 milestones: [
            //                     { milestoneName: "RawLead", milestoneNum: 33, totalCount: 13, uniqueCount: 8 },
            //                     { milestoneName: "DeepEngaged", milestoneNum: 432, totalCount: 8, uniqueCount: 6 },
            //                     { milestoneName: "LeadSubmitted", milestoneNum: 3543, totalCount: 4, uniqueCount: 3 },
            //                     { milestoneName: "LeadSubmitted", milestoneNum: 0, totalCount: 4, uniqueCount: 3 },
            //                 ],
            //             },
            //             {
            //                 adId: "1202439447580365",
            //                 adName: "SIP_2_asdfd_54",
            //                 spend: 964,
            //                 reach: 7170,
            //                 impressions: 9532,
            //                 clicks: 81,
            //                 clids: 13,
            //                 milestones: [
            //                     { milestoneName: "RawLead", milestoneNum: 33, totalCount: 13, uniqueCount: 8 },
            //                     { milestoneName: "DeepEngaged", milestoneNum: 432, totalCount: 8, uniqueCount: 6 },
            //                     { milestoneName: "LeadSubmitted", milestoneNum: 3543, totalCount: 4, uniqueCount: 3 },
            //                     { milestoneName: "LeadSubmitted", milestoneNum: 0, totalCount: 4, uniqueCount: 3 },
            //                 ],
            //             },
            //             {
            //                 adId: "1202434694470365",
            //                 adName: "SIP_asfd2_ad_54",
            //                 spend: 964,
            //                 reach: 7170,
            //                 impressions: 9532,
            //                 clicks: 81,
            //                 clids: 13,
            //                 milestones: [
            //                     { milestoneName: "RawLead", milestoneNum: 33, totalCount: 13, uniqueCount: 8 },
            //                     { milestoneName: "DeepEngaged", milestoneNum: 432, totalCount: 8, uniqueCount: 6 },
            //                     { milestoneName: "LeadSubmitted", milestoneNum: 3543, totalCount: 4, uniqueCount: 3 },
            //                     { milestoneName: "LeadSubmitted", milestoneNum: 0, totalCount: 4, uniqueCount: 3 },
            //                 ],
            //             },
            //             {
            //                 adId: "1202434694475803",
            //                 adName: "SIP_2_ad_5sdfs4",
            //                 spend: 964,
            //                 reach: 7170,
            //                 impressions: 9532,
            //                 clicks: 81,
            //                 clids: 13,
            //                 milestones: [
            //                     { milestoneName: "RawLead", milestoneNum: 33, totalCount: 13, uniqueCount: 8 },
            //                     { milestoneName: "DeepEngaged", milestoneNum: 432, totalCount: 8, uniqueCount: 6 },
            //                     { milestoneName: "LeadSubmitted", milestoneNum: 3543, totalCount: 4, uniqueCount: 3 },
            //                     { milestoneName: "LeadSubmitted", milestoneNum: 0, totalCount: 4, uniqueCount: 3 },
            //                 ],
            //             },
            //         ],
            //     }
            // }

            if (res?.meta?.code === 200 && res.data) {
                const campaignName = res.data.campaignName || campaigns.find((c: any) => c.campaignId === overviewFilters.campaignId)?.campaignName || overviewFilters.campaignId;

                const ads = res.data.ads || [];
                if (ads.length === 0) {
                    message.info('No ads found for the selected campaign');
                    setAdComparisonData([]);
                    setAdComparisonCampaignData({});
                    return;
                }

                const allAdData = ads.map((ad: any) => ({
                    adId: ad.adId,
                    adName: ad.adName,
                    thumbnailUrl: ad.thumbnailUrl || ad.adCreativeImageLink || ad.adVideoThumbnailUrl || '',
                    campaignName,
                    cost: { spend: ad.spend },
                    performance: {
                        reach: ad.reach,
                        impressions: ad.impressions,
                        clicks: ad.clicks,
                        lead: ad.clids
                    },
                    botAnalytics: {
                        botId: res.data.botId || '', // Just in case it's added later
                        milestoneUserCounts: ad.milestones?.map((m: any) => ({
                            milestoneName: m.milestoneName,
                            milestoneNum: m.milestoneNum,
                            userCount: m.totalCount,
                            uniqueUserCount: m.uniqueCount
                        })) || []
                    }
                }));

                setAdComparisonData(allAdData);
                setAdComparisonCampaignData(res.data);
            } else {
                setAdComparisonData([]);
                setAdComparisonCampaignData({});
                message.info('No ad comparison data found');
            }
        } catch (error: any) {
            // console.error('Error fetching ad comparison data:', error?.response?.data?.meta?.message);
            handleApiError(error, 'ad-comparison', error?.response?.data?.meta?.message || 'Error fetching ad comparison data');
        } finally {
            setAdComparisonLoading(false);
        }
    }, [overviewFilters, campaigns, currentOrg]);

    const handleRemoveComparison = (key: number) => {
        setComparisonList(prev => prev.filter(item => item.key !== key));
    };

    const comparisonPerformanceChart = useMemo<ChartData<'bar', number[], string>>(() => {
        return {
            labels: comparisonList.map(i => i.campaignName),
            datasets: [
                {
                    label: 'Impressions',
                    data: comparisonList.map(i => Number(i.impressions || 0)),
                    backgroundColor: '#1f2937',
                },
                {
                    label: 'Link Clicks',
                    data: comparisonList.map(i => Number(i.clicks || 0)),
                    backgroundColor: '#4b5563',
                },
                {
                    label: 'Leads',
                    data: comparisonList.map(i => Number(i.lead || 0)),
                    backgroundColor: '#9ca3af',
                },
            ],
        };
    }, [comparisonList]);

    const comparisonCostChart = useMemo<ChartData<'bar', number[], string>>(() => {
        return {
            labels: comparisonList.map(i => i.campaignName),
            datasets: [
                {
                    label: 'CPL (₹)',
                    data: comparisonList.map(i => i.costPerLead ? Number(i.costPerLead) : 0),
                    backgroundColor: '#111827',
                },
                {
                    label: 'CPC (₹)',
                    data: comparisonList.map(i => i.cpc ? Number(i.cpc) : 0),
                    backgroundColor: '#6b7280',
                },
            ],
        };
    }, [comparisonList]);

    const comparisonChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                labels: {
                    font: { size: 11 },
                    color: '#374151',
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: '#f3f4f6' },
                ticks: { color: '#6b7280', font: { size: 11 } },
            },
            x: {
                grid: { display: false },
                ticks: { color: '#6b7280', font: { size: 11 } },
            },
        },
    };


    // Effects
    const initialLoadRef = React.useRef(true);
    const hasSubmittedRef = React.useRef(false);

    // 1. Initialize filters from URL params OR defaults (runs once on mount)
    useEffect(() => {
        const urlOrgId = searchParams.get('orgId');
        const urlAdAccountId = searchParams.get('adAccountId');
        const urlCampaignId = searchParams.get('campaignId');

        if (urlOrgId && urlAdAccountId && urlCampaignId) {
            // URL params exist - use them
            setOverviewFilters(prev => ({
                ...prev,
                orgId: urlOrgId,
                adAccountId: urlAdAccountId,
                campaignId: urlCampaignId
            }));
        } else if (selectedAdAccount?.id && currentOrg?.orgId) {
            // No URL params - use defaults from context
            setOverviewFilters(prev => ({
                ...prev,
                adAccountId: selectedAdAccount.id,
                orgId: currentOrg.orgId
            }));
        }
    }, []); // Empty deps - run once on mount

    // 2. Update filters when selectedAdAccount or currentOrg changes (but only if no URL params)
    useEffect(() => {
        const hasUrlParams = searchParams.get('orgId') || searchParams.get('adAccountId');

        if (!hasUrlParams && selectedAdAccount?.id && currentOrg?.orgId) {
            setOverviewFilters(prev => ({
                ...prev,
                adAccountId: selectedAdAccount.id,
                orgId: currentOrg.orgId
            }));
        }
    }, [selectedAdAccount?.id, currentOrg?.orgId, searchParams]);

    // 3. Fetch campaigns when ad account changes
    useEffect(() => {
        if (overviewFilters.adAccountId && overviewFilters.orgId) {
            fetchCampaignData(overviewFilters.adAccountId);
        }
    }, [overviewFilters.adAccountId, overviewFilters.orgId]);

    // 4. Auto-submit when filters are ready
    useEffect(() => {
        const urlOrgId = searchParams.get('orgId');
        const urlAdAccountId = searchParams.get('adAccountId');
        const urlCampaignId = searchParams.get('campaignId');
        const hasUrlParams = urlOrgId && urlAdAccountId && urlCampaignId;

        // Only auto-submit in overview tab when we have required filters
        if (activeTab === '1' &&
            overviewFilters.adAccountId &&
            overviewFilters.orgId &&
            overviewFilters.campaignId) {

            // If we have URL params and haven't submitted yet
            if (hasUrlParams && !hasSubmittedRef.current) {
                hasSubmittedRef.current = true;
                handleSubmit();
            }
        }
    }, [
        overviewFilters.adAccountId,
        overviewFilters.orgId,
        overviewFilters.campaignId,
        activeTab,
        searchParams
    ]);

    // 5. Reset submission flag when URL params are cleared
    useEffect(() => {
        const hasUrlParams = searchParams.get('orgId') ||
            searchParams.get('adAccountId') ||
            searchParams.get('campaignId');

        if (!hasUrlParams) {
            hasSubmittedRef.current = false;
        }
    }, [searchParams]);

    // 6. Initialize comparison filters and WABA data
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                if (currentOrg?.orgId) {
                    setComparisonFilters(p => ({
                        ...p,
                        orgId: currentOrg.orgId,
                        adAccountId: selectedAdAccount?.id || ''
                    }));

                    // Load WABA details
                    if (orgWabaData.length > 0) {
                        const currentOrgWaba = orgWabaData.find(o => o.orgId === currentOrg.orgId);
                        if (currentOrgWaba?.wabaDetails?.length) {
                            setWabaDetails(currentOrgWaba.wabaDetails as any);
                            if (currentOrgWaba.wabaDetails[0]?.clientId) {
                                setSelectedWabaId(currentOrgWaba.wabaDetails[0].clientId);
                            }
                        }
                    }
                }
            } catch (e) {
                handleApiError(e, 'dashboard-initialization', 'Failed to initialise dashboard');
            } finally {
                setLoading(false);
            }
        };

        if (currentOrg?.orgId) {
            init();
        }
    }, [currentOrg?.orgId, selectedAdAccount?.id, orgWabaData]);

    // 7. Set breadcrumbs (separate effect)
    useEffect(() => {
        setBreadcrumbs([{ title: <div className="text-lg">Meta Analytics</div> }]);
    }, [setBreadcrumbs]);

    // 8. Fetch campaigns for comparison tab
    useEffect(() => {
        if (comparisonFilters.adAccountId && comparisonFilters.orgId) {
            fetchCampaignData(comparisonFilters.adAccountId);
        }
    }, [comparisonFilters.adAccountId, comparisonFilters.orgId]);

    const getActionMetric = (aliases: string[]) => {
        const aliasSet = new Set(aliases.map(normalizeMetricKey));
        let found = false;
        const total = (performance.actions ?? []).reduce((sum, action) => {
            if (!aliasSet.has(normalizeMetricKey(action.actionType))) return sum;

            found = true;
            return sum + (parseMetricValue(action.value) ?? 0);
        }, 0);

        return found ? total : null;
    };

    const getMilestoneMetric = (aliases: string[]) => {
        const normalizedAliases = aliases.map(normalizeMetricKey);

        return (botAnalytics.milestoneUserCounts ?? []).reduce<number | null>((total, milestone) => {
            const milestoneName = normalizeMetricKey(milestone.milestoneName);
            if (!normalizedAliases.some((alias) => milestoneName.startsWith(alias))) return total;

            const userCount = parseMetricValue(milestone.userCount);
            if (userCount === null) return total;

            return (total ?? 0) + userCount;
        }, null);
    };

    const getMilestoneValueFromCounts = (milestoneUserCounts: MilestoneCount[] = [], aliases: string[]) => {
        const normalizedAliases = aliases.map(normalizeMetricKey);

        return milestoneUserCounts.reduce<number | null>((total, milestone) => {
            const milestoneName = normalizeMetricKey(milestone.milestoneName);
            if (!normalizedAliases.some((alias) => milestoneName.startsWith(alias))) return total;

            const userCount = parseMetricValue(milestone.userCount);
            if (userCount === null) return total;

            return (total ?? 0) + userCount;
        }, null);
    };

    const getAllCampaignMilestoneMetric = (aliases: string[]) => {
        let found = false;
        const total = allCampaignBotAnalytics.analytics.reduce((sum, campaignAnalytics) => {
            const campaignTotal = getMilestoneValueFromCounts(campaignAnalytics.milestoneUserCounts, aliases);
            if (campaignTotal === null) return sum;

            found = true;
            return sum + campaignTotal;
        }, 0);

        return found ? total : null;
    };

    const getAllCampaignBotName = (botId: string, fallback: string) => {
        return fallback || allCampaignBotAnalytics.bots.find((bot) => bot.botId === botId)?.name || 'Unnamed Bot';
    };

    const spendValue = parseMetricValue(cost.spend);
    const impressionCount = parseMetricValue(performance.impressions);
    const linkClickCount = parseMetricValue(performance.clicks);
    const conversationStartCount = parseMetricValue(performance.totalConversation);
    const totalCpm = parseMetricValue(cost.cpm) ?? (
        spendValue !== null && impressionCount !== null && impressionCount > 0
            ? (spendValue / impressionCount) * 1000
            : null
    );
    const rawLeadCount = getMilestoneMetric(['RawLead', 'Raw Lead', 'Raw Leads']);
    const deepEngagedCount = getMilestoneMetric(['DeepEngaged', 'Deep Engaged']);
    const qualifiedLeadCount = getMilestoneMetric(['LeadSubmitted', 'QualifiedLead', 'Qualified Lead', 'Qualified Leads']);
    const allCampaignRawLeadCount = getAllCampaignMilestoneMetric(['RawLead', 'Raw Lead', 'Raw Leads']);
    const allCampaignDeepEngagedCount = getAllCampaignMilestoneMetric(['DeepEngaged', 'Deep Engaged']);
    const allCampaignQualifiedLeadCount = getAllCampaignMilestoneMetric(['LeadSubmitted', 'QualifiedLead', 'Qualified Lead', 'Qualified Leads']);
    const allCampaignLiveBotCount = allCampaignBotAnalytics.bots.filter((bot) => bot.live).length;
    const isBotAnalyticsDisabled = overviewFilters.campaignId === 'all';

    const adAnalyticsMetrics: MetricCardProps[] = [
        // {
        //     title: 'Total Spend',
        //     value: formatCurrency(spendValue),
        //     tooltip: 'Spend against budget on ads served and Ability to Purchase data',
        // },
        {
            title: 'Impressions',
            value: formatNumber(impressionCount),
        },
        // {
        //     title: 'CPM',
        //     value: formatCurrency(totalCpm, 2),
        //     tooltip: 'Total CPM including ads and data unit costs',
        // },
        {
            title: 'Reach',
            value: formatNumber(performance.reach),
        },
        {
            title: 'Link Clicks',
            value: formatNumber(linkClickCount),
            tooltip: 'Number of times open whatsapp link clicked, total clicks will be higher',
        },
        {
            title: 'Link Click CTR',
            value: formatPercent(calculateRate(linkClickCount, impressionCount)),
            tooltip: 'Number of times open whatsapp link clicked, total CTR will be higher',
        },
        // {
        //     title: 'Link Click CPC',
        //     value: formatCurrency(calculateCost(spendValue, linkClickCount), 2),
        //     tooltip: 'Number of times open whatsapp link clicked, total CPC will be lower',
        // },
        {
            title: 'Unique Link Click',
            value: formatNumber(performance.uniqueClicks),
            tooltip: 'Unique users that have clicked the whatsapp open link',
        },
        {
            title: 'Conversation Starts',
            value: formatNumber(conversationStartCount),
            tooltip: 'Number of times users that have clicked the whatsapp open link of an ad served on Meta, gone to whatsapp and entered the pre-filled text',
        },
        // {
        //     title: 'Cost Per Conversation Start',
        //     value: formatCurrency(calculateCost(spendValue, conversationStartCount), 2),
        // },
        {
            title: 'Link Click to Conversation Start Rate',
            value: formatPercent(calculateRate(conversationStartCount, linkClickCount)),
        },
        {
            title: 'Onsite Conversion.Post Net Comment',
            value: formatNumber(getActionMetric(['onsite_conversion.post_net_comment', 'onsite conversion.post net comment'])),
        },
        {
            title: 'Comment',
            value: formatNumber(getActionMetric(['comment'])),
        },
        {
            title: 'Onsite Conversion.Post Net Like',
            value: formatNumber(getActionMetric(['onsite_conversion.post_net_like', 'onsite conversion.post net like'])),
        },
        {
            title: 'Onsite Conversion.Post Unlike',
            value: formatNumber(getActionMetric(['onsite_conversion.post_unlike', 'onsite conversion.post unlike'])),
        },
    ];

    const adAnalyticsCostMetric: MetricCardProps[] = [
        {
            title: 'Total Spend',
            value: formatCurrency(spendValue),
            tooltip: 'Spend against budget on ads served and Ability to Purchase data',
        },
        {
            title: 'CPM',
            value: formatCurrency(totalCpm, 2),
            tooltip: 'Total CPM including ads and data unit costs',
        },
        {
            title: 'Link Click CPC',
            value: formatCurrency(calculateCost(spendValue, linkClickCount), 2),
            tooltip: 'Number of times open whatsapp link clicked, total CPC will be lower',
        },
        {
            title: 'Cost Per Conversation Start',
            value: formatCurrency(calculateCost(spendValue, conversationStartCount), 2),
        }
    ];

    const botAnalyticsMetrics: MetricCardProps[] = [
        {
            title: 'Raw Leads',
            value: formatNumber(rawLeadCount),
            tooltip: 'Number of times users have come to whatsapp and executed the Raw Lead milestone - either via the ad campaign or directly (without clkid)',
            disabled: isBotAnalyticsDisabled,
        },
        {
            title: 'Deep Engaged',
            value: formatNumber(deepEngagedCount),
            tooltip: 'Number of times users have come to whatsapp and executed the Deep Engaged milestone - either via the ad campaign or directly (without clkid)',
            disabled: isBotAnalyticsDisabled,
        },
        {
            title: 'Qualified Leads (LeadSubmitted)',
            value: formatNumber(qualifiedLeadCount),
            tooltip: 'Number of times users have come to whatsapp and executed the Qualified Lead milestone - either via the ad campaign or directly (without clkid)',
            disabled: isBotAnalyticsDisabled,
        },
        {
            title: 'Conversation Start to Qualified Lead Rate',
            value: formatPercent(calculateRate(qualifiedLeadCount, conversationStartCount)),
            subtitle: isBotAnalyticsDisabled ? '' : undefined,
            disabled: isBotAnalyticsDisabled,
        },
        {
            title: 'Cost Per Qualified Leads',
            value: formatCurrency(calculateCost(spendValue, qualifiedLeadCount), 2),
            subtitle: isBotAnalyticsDisabled ? '' : undefined,
            disabled: isBotAnalyticsDisabled,
        },
    ];

    const allCampaignBotAnalyticsMetrics: MetricCardProps[] = [
        {
            title: 'Campaigns With Bot Analytics',
            value: hasFetchedAllCampaignBotAnalytics ? formatNumber(allCampaignBotAnalytics.analytics.length) : '-',
        },
        {
            title: 'Bots',
            value: hasFetchedAllCampaignBotAnalytics ? formatNumber(allCampaignBotAnalytics.bots.length) : '-',
            subtitle: hasFetchedAllCampaignBotAnalytics ? `${formatNumber(allCampaignLiveBotCount)} live` : undefined,
        },
        {
            title: 'Raw Leads',
            value: hasFetchedAllCampaignBotAnalytics ? formatNumber(allCampaignRawLeadCount) : '-',
            tooltip: 'Total Raw Lead milestone users across all campaigns for the selected ad account',
        },
        {
            title: 'Deep Engaged',
            value: hasFetchedAllCampaignBotAnalytics ? formatNumber(allCampaignDeepEngagedCount) : '-',
            tooltip: 'Total Deep Engaged milestone users across all campaigns for the selected ad account',
        },
        {
            title: 'Qualified Leads',
            value: hasFetchedAllCampaignBotAnalytics ? formatNumber(allCampaignQualifiedLeadCount) : '-',
            tooltip: 'Total LeadSubmitted or Qualified Lead milestone users across all campaigns for the selected ad account',
        },
    ];

    // UI Components
    const MetricCard = ({ title, value, subtitle, tooltip, disabled = false }: MetricCardProps) => (
        <div className={`rounded-lg p-3 border transition-all duration-200 ${disabled
            ? 'bg-gray-50 border-gray-200 opacity-60'
            : 'bg-white border-gray-200 hover:border-gray-400'
            }`}
        >
            <div className="flex items-start justify-between gap-2 mb-1">
                <p className={`text-xs leading-snug ${disabled ? 'text-gray-400' : 'text-gray-500'}`}>{title}</p>
                {tooltip && (
                    <Tooltip title={tooltip}>
                        <InfoCircleOutlined className={`text-xs shrink-0 mt-0.5 ${disabled ? 'text-gray-300' : 'text-gray-400'}`} />
                    </Tooltip>
                )}
            </div>
            <p className={`text-2xl font-bold ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>{disabled ? '-' : value}</p>
            {subtitle && <p className={`text-xs mt-0.5 ${disabled ? 'text-gray-400' : 'text-gray-400'}`}>{subtitle}</p>}
        </div>
    );

    return (
        <CommonLayout>
            <Modal open={loading2 || adComparisonLoading} closable={false} footer={null} centered maskClosable={false}>
                <div className="flex flex-col items-center justify-center p-8">
                    <LoadingOutlined className="text-4xl text-gray-900 mb-4" spin />
                    <p className="text-lg font-medium">Loading analytics...</p>
                </div>
            </Modal>

            <div className="res-px py-4">
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    className="mt-2"
                    type="card"
                    items={[
                        {
                            key: '1',
                            label: 'Overview',
                            children: (
                                <div className="p-4 bg-gray-50">
                                    <div className="max-w-7xl mx-auto space-y-4">
                                        {/* Header */}
                                        <div className="flex items-center justify-between mb-2">
                                            <div>
                                                <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
                                                <p className="text-sm text-gray-500">Campaign performance overview</p>
                                            </div>
                                        </div>

                                        {/* Filters */}
                                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-2">
                                                <div>
                                                    <Typography.Text type="secondary" className="mb-1 text-s">Meta Ad Account</Typography.Text>
                                                    <Select
                                                        value={metaConnectStatus ? overviewFilters.adAccountId : null}
                                                        className="w-full"
                                                        placeholder="Meta Ad Account"
                                                        onChange={(v) => handleFilterChange('adAccountId', v)}
                                                        disabled={!canSelectMetaAdAccountInReports || !metaConnectStatus || user?.userRole != "admin"}
                                                        size="large"
                                                    >
                                                        {adAccountList.map((a) => (
                                                            <Option key={a.id} value={a.id}>{a.name}</Option>
                                                        ))}
                                                    </Select>
                                                </div>
                                                <div>
                                                    <Typography.Text type="secondary" className="mb-1 text-s">Account</Typography.Text>
                                                    <Select
                                                        value={overviewFilters.orgId}
                                                        className="w-full"
                                                        placeholder="Account"
                                                        onChange={(v) => handleFilterChange('orgId', v)}
                                                        size="large"
                                                    >
                                                        {availableOrgs.map((org) => (
                                                            <Option key={org.orgId} value={org.orgId}>{org.orgName}</Option>
                                                        ))}
                                                    </Select>
                                                </div>

                                                <div className="xl:col-span-2">
                                                    <Typography.Text type="secondary" className="mb-1 text-s">Campaign</Typography.Text>
                                                    <Select
                                                        value={metaConnectStatus ? overviewFilters.campaignId : null}
                                                        className="w-full"
                                                        placeholder="Campaign"
                                                        onChange={(v) => handleFilterChange('campaignId', v)}
                                                        disabled={!metaConnectStatus}
                                                        size="large"
                                                        virtual={false}
                                                    >
                                                        {campaigns.map((c) => (
                                                            <Option key={c.campaignId} value={c.campaignId}>{c.campaignName}</Option>
                                                        ))}
                                                    </Select>
                                                </div>

                                                <div>
                                                    <Typography.Text type="secondary" className="mb-1 text-s">Date Range</Typography.Text>
                                                    <RangePicker
                                                        value={overviewFilters.dateRange}
                                                        onChange={(d) => handleFilterChange('dateRange', d || [null, null])}
                                                        className="w-full"
                                                        size="large"
                                                    />
                                                </div>

                                                <div className="order-first justify-self-end md:col-start-2 xl:order-last xl:col-start-auto xl:justify-self-auto">
                                                    <Typography.Text className="mb-1 text-white">Account</Typography.Text>
                                                    <Button
                                                        type="primary"
                                                        icon={<SyncOutlined spin={loading2} />}
                                                        className="bg-[#1F344C] hover:bg-[#1F344C] border-[#1F344C] w-auto xl:w-full"
                                                        onClick={handleSubmit}
                                                        disabled={!metaConnectStatus}
                                                        size="large"
                                                    >
                                                        Submit
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>


                                        {/* Ad Analytics */}
                                        <div className="bg-white rounded-lg p-4 border border-gray-300">
                                            <h2 className="text-lg font-bold text-gray-900 mb-3">Ad Metric Analytics</h2>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
                                                {adAnalyticsMetrics.map((metric) => (
                                                    <MetricCard key={metric.title} {...metric} />
                                                ))}
                                            </div>
                                        </div>

                                        {/* Ad Analytics */}
                                        <div className="bg-white rounded-lg p-4 border border-gray-300">
                                            <h2 className="text-lg font-bold text-gray-900 mb-3">Ad Cost Analytics</h2>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
                                                {adAnalyticsCostMetric.map((metric) => (
                                                    <MetricCard key={metric.title} {...metric} />
                                                ))}
                                            </div>
                                        </div>

                                        {/* Bot Analytics */}
                                        <div className="bg-white rounded-lg p-4 border border-gray-300">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                                                <h2 className="text-lg font-bold text-gray-900">Bot Analytics</h2>
                                                {isBotAnalyticsDisabled ? (
                                                    <span className="w-fit px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                                        All Campaigns
                                                    </span>
                                                ) : botAnalytics.botName ? (
                                                    <span
                                                        className={`px-2 py-1 text-xs font-medium rounded-full ${botAnalytics.live
                                                            ? 'bg-green-100 text-green-700'
                                                            : 'bg-red-100 text-red-700'
                                                            }`}
                                                    >
                                                        {botAnalytics.botName}
                                                    </span>
                                                ) : null}
                                            </div>
                                            {isBotAnalyticsDisabled ? (
                                                <div className="space-y-4">
                                                    {/* <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
                                                        {allCampaignBotAnalyticsMetrics.map((metric) => (
                                                            <MetricCard key={metric.title} {...metric} />
                                                        ))}
                                                    </div> */}

                                                    {allCampaignBotAnalytics.bots.length > 0 && (
                                                        <div className="flex flex-wrap gap-2">
                                                            {allCampaignBotAnalytics.bots.map((bot) => (
                                                                <span
                                                                    key={bot.botId}
                                                                    className={`px-3 py-1 text-xs font-medium rounded-full border ${bot.live
                                                                        ? 'bg-green-50 text-green-700 border-green-100'
                                                                        : 'bg-gray-50 text-gray-600 border-gray-200'
                                                                        }`}
                                                                >
                                                                    {bot.name} - {bot.live ? 'Live' : 'Not live'}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {hasFetchedAllCampaignBotAnalytics && allCampaignBotAnalytics.analytics.length > 0 ? (
                                                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                                                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                                <thead className="bg-gray-50">
                                                                    <tr>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Campaign</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Bot</th>
                                                                        {/* <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Raw Leads</th> */}
                                                                        {/* <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Deep Engaged</th> */}
                                                                        {/* <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Qualified Leads</th> */}
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Milestones</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-gray-100 bg-white">
                                                                    {allCampaignBotAnalytics.analytics.map((item, index) => {
                                                                        // const rowRawLeadCount = getMilestoneValueFromCounts(item.milestoneUserCounts, ['RawLead', 'Raw Lead', 'Raw Leads']);
                                                                        // const rowDeepEngagedCount = getMilestoneValueFromCounts(item.milestoneUserCounts, ['DeepEngaged', 'Deep Engaged']);
                                                                        // const rowQualifiedLeadCount = getMilestoneValueFromCounts(item.milestoneUserCounts, ['LeadSubmitted', 'QualifiedLead', 'Qualified Lead', 'Qualified Leads']);

                                                                        return (
                                                                            <tr key={`${item.botId}-${item.campaignId}-${index}`}>
                                                                                <td className="px-3 py-3 align-top">
                                                                                    <p className="font-medium text-gray-900">{item.campaignName}</p>
                                                                                    <p className="text-xs text-gray-400">{item.campaignId}</p>
                                                                                </td>
                                                                                <td className="px-3 py-3 align-top">
                                                                                    <div className="flex flex-col gap-1">
                                                                                        <span className="font-medium text-gray-800">{getAllCampaignBotName(item.botId, item.botName)}</span>
                                                                                        <span
                                                                                            className={`w-fit px-2 py-0.5 text-xs font-medium rounded-full ${item.live
                                                                                                ? 'bg-green-100 text-green-700'
                                                                                                : 'bg-gray-100 text-gray-600'
                                                                                                }`}
                                                                                        >
                                                                                            {item.live ? 'Live' : 'Not live'}
                                                                                        </span>
                                                                                    </div>
                                                                                </td>
                                                                                {/* <td className="px-3 py-3 text-right align-top font-semibold text-gray-900">{formatNumber(rowRawLeadCount)}</td> */}
                                                                                {/* <td className="px-3 py-3 text-right align-top font-semibold text-gray-900">{formatNumber(rowDeepEngagedCount)}</td> */}
                                                                                {/* <td className="px-3 py-3 text-right align-top font-semibold text-gray-900">{formatNumber(rowQualifiedLeadCount)}</td> */}
                                                                                <td className="px-3 py-3 align-top">
                                                                                    <div className="flex min-w-[220px] flex-wrap gap-1.5">
                                                                                        {item.milestoneUserCounts.length > 0 ? (
                                                                                            item.milestoneUserCounts.map((milestone, index) => (
                                                                                                <span
                                                                                                    key={`${item.campaignId}-${milestone.milestoneName}-${index}`}
                                                                                                    className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700"
                                                                                                >
                                                                                                    {milestone.milestoneName}: {formatNumber(milestone.userCount)}
                                                                                                </span>
                                                                                            ))
                                                                                        ) : (
                                                                                            <span className="text-xs text-gray-400">No milestones</span>
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center">
                                                            <p className="text-sm font-medium text-gray-700">
                                                                {hasFetchedAllCampaignBotAnalytics
                                                                    ? 'No bot analytics found for all campaigns'
                                                                    : 'Submit filters to view all campaign bot analytics'}
                                                            </p>
                                                            <p className="mt-1 text-xs text-gray-500">
                                                                This section uses bot analytics for every campaign in the selected Meta ad account.
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
                                                    {botAnalyticsMetrics.map((metric) => (
                                                        <MetricCard key={metric.title} {...metric} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        },
                        {
                            key: '2',
                            label: 'Compare',
                            children: (
                                <div className="p-4 bg-gray-50 min-h-screen">
                                    <div className="max-w-7xl mx-auto space-y-4">

                                        {/* Header */}
                                        <div>
                                            <h1 className="text-2xl font-bold text-gray-900">
                                                Campaign Comparison
                                            </h1>
                                            <p className="text-sm text-gray-500">
                                                Compare performance across campaigns (max 3)
                                            </p>
                                        </div>

                                        {/* Filters Modal */}
                                        <Modal
                                            title="Add Campaign to Compare"
                                            open={isComparisonModalOpen}
                                            onCancel={() => setIsComparisonModalOpen(false)}
                                            footer={null}
                                            width={600}
                                        >
                                            <div className="space-y-4 py-4">
                                                <div>
                                                    <Typography.Text type="secondary" className="mb-1 text-s">Meta Ad Account</Typography.Text>
                                                    <Select
                                                        value={comparisonFilters.adAccountId}
                                                        className="w-full"
                                                        placeholder="Meta Ad Account"
                                                        onChange={(v) => handleFilterChange('adAccountId', v, 'comparison')}
                                                        size="large"
                                                        virtual={false}
                                                        popupMatchSelectWidth={false}
                                                    >
                                                        {adAccountList.map((a) => (
                                                            <Option key={a.id} value={a.id}>
                                                                {a.name}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </div>

                                                <div>
                                                    <Typography.Text type="secondary" className="mb-1 text-s">Account</Typography.Text>
                                                    <Select
                                                        value={comparisonFilters.orgId}
                                                        className="w-full"
                                                        placeholder="Account"
                                                        onChange={(v) => handleFilterChange('orgId', v, 'comparison')}
                                                        size="large"
                                                    >
                                                        {availableOrgs.map((org) => (
                                                            <Option key={org.orgId} value={org.orgId}>
                                                                {org.orgName}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </div>

                                                <div>
                                                    <Typography.Text type="secondary" className="mb-1 text-s">Campaign</Typography.Text>
                                                    <Select
                                                        value={comparisonFilters.campaignId === 'all' ? null : comparisonFilters.campaignId}
                                                        className="w-full"
                                                        placeholder="Campaign"
                                                        onChange={(v) => handleFilterChange('campaignId', v, 'comparison')}
                                                        size="large"
                                                        virtual={false}
                                                    >
                                                        {campaigns.map((c) => (
                                                            c.campaignId !== 'all' && <Option key={c.campaignId} value={c.campaignId}>
                                                                {c.campaignName}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </div>

                                                <div>
                                                    <Typography.Text type="secondary" className="mb-1 text-s">Date Range</Typography.Text>
                                                    <RangePicker
                                                        value={comparisonFilters.dateRange}
                                                        onChange={(d) => handleFilterChange('dateRange', d || [null, null], 'comparison')}
                                                        className="w-full"
                                                        size="large"
                                                    />
                                                </div>

                                                <div className="mt-6">
                                                    <Button
                                                        type="primary"
                                                        className="bg-[#1F344C] w-full"
                                                        onClick={() => handleSubmit()}
                                                        disabled={comparisonList.length >= 3 || !comparisonFilters.campaignId || comparisonFilters.campaignId === 'all'}
                                                        size="large"
                                                        loading={loading2}
                                                    >
                                                        Add to Compare
                                                    </Button>
                                                </div>
                                            </div>
                                        </Modal>

                                        {/* Comparison Cards */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {comparisonList.map((item) => (
                                                <div
                                                    key={item.key}
                                                    className="bg-white rounded-lg border border-gray-100 hover:shadow-md transition-all relative flex flex-col"
                                                >
                                                    {/* Remove */}
                                                    <button
                                                        onClick={() => handleRemoveComparison(item.key)}
                                                        className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100 z-10 bg-white"
                                                    >
                                                        <X className="w-4 h-4 text-gray-500" />
                                                    </button>

                                                    {/* Header info: Thumbnail + Name + Date */}
                                                    <div className="p-3 border-b border-gray-100 flex items-center gap-3">
                                                        {item.creativeThumbnailUrl ? (
                                                            <img
                                                                src={item.creativeThumbnailUrl}
                                                                alt="Creative"
                                                                className="w-12 h-12 object-cover rounded shadow-sm flex-shrink-0"
                                                            />
                                                        ) : (
                                                            <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                                                                <Target className="w-6 h-6 text-gray-400" />
                                                            </div>
                                                        )}
                                                        <div className="flex flex-col pr-6 overflow-hidden">
                                                            <h3 className="font-semibold text-gray-900 truncate" title={item.campaignName}>
                                                                {item.campaignName}
                                                            </h3>
                                                            <p className="text-xs text-gray-500 truncate">
                                                                {item.dateRangeFilter?.startDate
                                                                    ? `${item.dateRangeFilter.startDate} - ${item.dateRangeFilter.endDate}`
                                                                    : 'All Time'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="p-3 space-y-3 flex-1">
                                                        {/* Performance */}
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <MetricCard title="Impressions" value={item.impressions ? Number(item.impressions).toLocaleString() : '-'} />
                                                            <MetricCard title="Reach" value={item.reach ? Number(item.reach).toLocaleString() : '-'} />
                                                            <MetricCard title="Link Clicks" value={item.clicks ? Number(item.clicks).toLocaleString() : '-'} />
                                                            <MetricCard title="Leads" value={item.leads ? Number(item.leads).toLocaleString() : '-'} />
                                                        </div>

                                                        <Divider className="my-2" />

                                                        {/* Cost */}
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <MetricCard title="Spend" value={`₹${item.spend ? Number(item.spend).toLocaleString() : '-'}`} />
                                                            <MetricCard title="CPL" value={`₹${item.costPerLead ? Number(item.costPerLead).toFixed(2) : '-'}`} />
                                                            <MetricCard title="CTR" value={`${item.ctr ? Number(item.ctr).toFixed(2) : '-'}%`} />
                                                            <MetricCard title="CPC" value={`₹${item.cpc ? Number(item.cpc).toFixed(2) : '-'}`} />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            {comparisonList.length < 3 && (
                                                <div
                                                    onClick={() => setIsComparisonModalOpen(true)}
                                                    className="border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center p-6 cursor-pointer hover:border-gray-400 hover:bg-gray-50 min-h-[350px]"
                                                >
                                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                                        <Plus className="text-gray-500 w-6 h-6" />
                                                    </div>
                                                    <p className="text-gray-600 font-medium text-lg">Compare Ad</p>
                                                    <p className="text-gray-400 text-sm mt-1 text-center">Click to select an ad campaign to compare</p>
                                                </div>
                                            )}
                                        </div>
                                        {comparisonList.length > 0 && (
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                                {/* Performance Comparison */}
                                                <div className="bg-white rounded-lg p-4 border border-gray-100">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <TrendingUp className="w-4 h-4 text-gray-700" />
                                                        <h3 className="text-base font-bold text-gray-900">
                                                            Performance Comparison
                                                        </h3>
                                                    </div>
                                                    <div className="h-64">
                                                        <Bar
                                                            data={comparisonPerformanceChart}
                                                            options={comparisonChartOptions}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Cost Comparison */}
                                                <div className="bg-white rounded-lg p-4 border border-gray-100">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <TrendingDown className="w-4 h-4 text-gray-700" />
                                                        <h3 className="text-base font-bold text-gray-900">
                                                            Cost Efficiency
                                                        </h3>
                                                    </div>
                                                    <div className="h-64">
                                                        <Bar
                                                            data={comparisonCostChart}
                                                            options={comparisonChartOptions}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            )
                        },
                        {
                            key: '3',
                            label: 'Funnel',
                            children: (
                                <div className="p-4 bg-gray-50 min-h-screen">
                                    <div className="max-w-7xl mx-auto space-y-4">
                                        <div>
                                            <h1 className="text-2xl font-bold text-gray-900">
                                                Ads Comparison
                                            </h1>
                                            <p className="text-sm text-gray-500">
                                                Compare ad performances within a specific campaign
                                            </p>
                                        </div>

                                        {/* Filters */}
                                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-2">
                                                <div>
                                                    <Typography.Text type="secondary" className="mb-1 text-s">Meta Ad Account</Typography.Text>
                                                    <Select
                                                        value={metaConnectStatus ? overviewFilters.adAccountId : null}
                                                        className="w-full"
                                                        placeholder="Meta Ad Account"
                                                        onChange={(v) => handleFilterChange('adAccountId', v)}
                                                        disabled={!canSelectMetaAdAccountInReports || !metaConnectStatus}
                                                        size="large"
                                                    >
                                                        {adAccountList.map((a) => (
                                                            <Option key={a.id} value={a.id}>{a.name}</Option>
                                                        ))}
                                                    </Select>
                                                </div>
                                                <div>
                                                    <Typography.Text type="secondary" className="mb-1 text-s">Account</Typography.Text>
                                                    <Select
                                                        value={overviewFilters.orgId}
                                                        className="w-full"
                                                        placeholder="Account"
                                                        onChange={(v) => {
                                                            handleFilterChange('orgId', v)
                                                            // console.log(v)
                                                        }}
                                                        size="large"
                                                    >
                                                        {availableOrgs.map((org) => (
                                                            <Option key={org.orgId} value={org.orgId}>{org.orgName}</Option>
                                                        ))}
                                                    </Select>
                                                </div>

                                                <div className="xl:col-span-2">
                                                    <Typography.Text type="secondary" className="mb-1 text-s">Campaign</Typography.Text>
                                                    <Select
                                                        value={metaConnectStatus ? overviewFilters.campaignId : null}
                                                        className="w-full"
                                                        placeholder="Campaign"
                                                        onChange={(v) => handleFilterChange('campaignId', v)}
                                                        disabled={!metaConnectStatus}
                                                        size="large"
                                                        virtual={false}
                                                    >
                                                        {campaigns.map((c) => (
                                                            <Option key={c.campaignId} value={c.campaignId}>{c.campaignName}</Option>
                                                        ))}
                                                    </Select>
                                                </div>

                                                <div>
                                                    <Typography.Text type="secondary" className="mb-1 text-s">Date Range</Typography.Text>
                                                    <RangePicker
                                                        value={overviewFilters.dateRange}
                                                        onChange={(d) => handleFilterChange('dateRange', d || [null, null])}
                                                        className="w-full"
                                                        size="large"
                                                    />
                                                </div>

                                                <div className="order-first justify-self-end md:col-start-2 xl:order-last xl:col-start-auto xl:justify-self-auto">
                                                    <Typography.Text className="mb-1 text-white">Account</Typography.Text>
                                                    <Button
                                                        type="primary"
                                                        icon={<SyncOutlined spin={loading2} />}
                                                        className="bg-[#1F344C] hover:bg-[#1F344C] border-[#1F344C] w-auto xl:w-full"
                                                        onClick={handleAdComparisonSubmit}
                                                        disabled={!metaConnectStatus}
                                                        size="large"
                                                    >
                                                        Submit
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>

                                        {adComparisonData.length > 0 ? (
                                            <div className="bg-white rounded-lg p-4 border border-gray-100 overflow-x-auto">
                                                <AdComparisonTable data={adComparisonCampaignData} />
                                            </div>
                                        ) : (
                                            <div className="text-center py-12 text-gray-500 bg-white rounded-lg border border-gray-100">
                                                Select an Ad Account and Campaign to view Ads Comparison
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        }
                    ]}
                />
            </div>
        </CommonLayout>
    )
}

export default MetaAnalyticsDashboard;
